import { FC, useMemo } from 'react';
import { ReferenceField, TextField, useGetManyAggregate, useRecordContext } from 'react-admin';
import { Chip, Stack } from '@mui/material';

import { Hardware } from 'types/Hardware';
import { UpdateChannel } from 'types/UpdateChannel';
import { useHardwareFilterId } from './hardwareFilter';

interface UpdateChannelHardwareFieldProps {
  /** Read by `Datagrid` for the column header. */
  label?: string;
  /** Read by `Datagrid`. The column is computed, so pass `false`. */
  sortable?: boolean;
}

/**
 * Every hardware an update channel serves, one chip per distinct `hardware_id` in
 * `record.hardware_revisions` (TVR-2382). The chip for the list's hardware filter comes first
 * and is filled in the primary colour; the rest are outlined and sorted by name.
 *
 * Names come from `useGetManyAggregate`, which merges the ids of every row rendered in the same
 * tick into one `getMany('hardware')`. `useGetMany` (as `useHardwareNames` on the Show page uses)
 * would issue one request per row.
 *
 * Needs a ListContext: it reads the filter through `useHardwareFilterId`, whose `useListContext`
 * throws outside a list. Render it in a `List`/`Datagrid` (or a `ListContextProvider`), not on a
 * Show or Edit page.
 */
export const UpdateChannelHardwareField: FC<UpdateChannelHardwareFieldProps> = () => {
  const record = useRecordContext<UpdateChannel>();
  const filterHardwareId = useHardwareFilterId();

  // Memoised for a stable `ids` identity: useGetManyAggregate derives its placeholder data from it.
  const hardwareIds = useMemo(
    () => Array.from(new Set((record?.hardware_revisions ?? []).map((revision) => revision.hardware_id))),
    [record?.hardware_revisions],
  );

  const { data } = useGetManyAggregate<Hardware>('hardware', { ids: hardwareIds }, { enabled: hardwareIds.length > 0 });

  // Legacy rows without hardware_revisions keep the single hardware_id display, which is null
  // for family channels; those render an empty cell.
  if (hardwareIds.length === 0) {
    if (record?.hardware_id == null) return null;
    return (
      <ReferenceField source="hardware_id" reference="hardware">
        <TextField source="name" />
      </ReferenceField>
    );
  }

  // While the names load, or if the lookup fails, a chip shows the hardware id instead.
  const names = new Map<string, string>();
  (data ?? []).forEach((hardware) => {
    if (hardware) names.set(String(hardware.id), hardware.name ?? String(hardware.id));
  });
  const nameOf = (hardwareId: number): string => names.get(String(hardwareId)) ?? String(hardwareId);

  const others = hardwareIds
    .filter((hardwareId) => hardwareId !== filterHardwareId)
    .sort((a, b) => nameOf(a).localeCompare(nameOf(b), undefined, { numeric: true }));
  const ordered =
    filterHardwareId !== null && hardwareIds.includes(filterHardwareId) ? [filterHardwareId, ...others] : others;

  return (
    <Stack direction="row" spacing={0.5} useFlexGap sx={{ flexWrap: 'wrap' }}>
      {ordered.map((hardwareId) => {
        const isFiltered = hardwareId === filterHardwareId;
        return (
          <Chip
            key={hardwareId}
            size="small"
            label={nameOf(hardwareId)}
            color={isFiltered ? 'primary' : 'default'}
            variant={isFiltered ? 'filled' : 'outlined'}
          />
        );
      })}
    </Stack>
  );
};
